import { useEffect, useState, useCallback } from 'react';
import { MembershipManagmentB2BService } from '@sdk-point/talisis';

// Catálogos de membresía y addon compartidos entre el alta de usuarios (Altas /
// Details.jsx) y el cambio de membresía (Modificaciones / ChangeMembership.jsx).
// Ambos flujos usan EXACTAMENTE los mismos endpoints y el mismo mapeo:
//   - getCatalogMembership() -> { id: item.id,  label: item.description }
//   - getCatalogAddon()      -> { id: item.sku, label: item.name }
// Antes cada componente los pedía por su cuenta (y el modal, además, al abrirse,
// lo que causaba demora). Este hook centraliza la carga: la llamada al backend
// ocurre UNA sola vez por sesión y el resultado se reutiliza en ambos lugares.
//
// Los PLAZOS (period) NO se cachean aquí: dependen de la membresía elegida, así
// que se exponen vía getPeriods(membershipId) para pedirlos bajo demanda.

const MembershipManagmentB2BServiceSDK = new MembershipManagmentB2BService(
    process.env.REACT_APP_ENV_V2,
    process.env.REACT_APP_X_API_KEY_V2
);

// Caché a nivel de MÓDULO (persiste mientras la app esté viva, no por componente).
// `inflight` deduplica llamadas concurrentes: si dos componentes montan a la vez,
// comparten la misma promesa en vez de disparar dos peticiones.
let catalogsCache = null;
let inflightPromise = null;

const fetchCatalogs = async () => {
    const [membershipData, addonData] = await Promise.all([
        MembershipManagmentB2BServiceSDK.getCatalogMembership(),
        MembershipManagmentB2BServiceSDK.getCatalogAddon(),
    ]);

    const membershipOptions = membershipData?.success
        ? membershipData.data.map((item) => ({ id: item.id, label: item.description }))
        : [];
    const addonOptions = addonData?.success
        ? addonData.data.map((item) => ({ id: item.sku, label: item.name }))
        : [];

    return { membershipOptions, addonOptions };
};

const loadCatalogsOnce = () => {
    if (catalogsCache) return Promise.resolve(catalogsCache);
    if (inflightPromise) return inflightPromise;

    inflightPromise = fetchCatalogs()
        .then((result) => {
            catalogsCache = result;
            inflightPromise = null;
            return result;
        })
        .catch((err) => {
            // No cachear un error: permitir reintento en el próximo montaje.
            inflightPromise = null;
            throw err;
        });

    return inflightPromise;
};

/**
 * Hook que entrega los catálogos de membresía y addon ya cargados (o cargándolos
 * una sola vez de forma compartida). Devuelve además `getPeriods` para los plazos,
 * que dependen de la membresía elegida y por eso no se cachean.
 */
export default function useMembershipCatalogs() {
    const [membershipOptions, setMembershipOptions] = useState(() => catalogsCache?.membershipOptions || []);
    const [addonOptions, setAddonOptions] = useState(() => catalogsCache?.addonOptions || []);
    const [loadingCatalogs, setLoadingCatalogs] = useState(() => !catalogsCache);

    useEffect(() => {
        // Si ya está en caché, no hay nada que esperar.
        if (catalogsCache) {
            setMembershipOptions(catalogsCache.membershipOptions);
            setAddonOptions(catalogsCache.addonOptions);
            setLoadingCatalogs(false);
            return;
        }

        let cancelled = false;
        setLoadingCatalogs(true);
        loadCatalogsOnce()
            .then((result) => {
                if (cancelled) return;
                setMembershipOptions(result.membershipOptions);
                setAddonOptions(result.addonOptions);
            })
            .catch((err) => {
                console.error('[useMembershipCatalogs] Error cargando catálogos:', err);
            })
            .finally(() => {
                if (!cancelled) setLoadingCatalogs(false);
            });

        return () => { cancelled = true; };
    }, []);

    // Plazos (period): dependen de la membresía. Se piden bajo demanda y se mapean
    // igual que en ambos flujos: { id: item.sku, label: item.name }.
    const getPeriods = useCallback(async (membershipId) => {
        if (!membershipId) return [];
        try {
            const periodData = await MembershipManagmentB2BServiceSDK.getCatalogPeriod({ id: membershipId });
            if (periodData?.success) {
                return periodData.data.map((item) => ({ id: item.sku, label: item.name }));
            }
        } catch (err) {
            console.error('[useMembershipCatalogs] Error cargando plazos:', err);
        }
        return [];
    }, []);

    return { membershipOptions, addonOptions, loadingCatalogs, getPeriods };
}
